import { useState, useEffect, useRef } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { useForecast } from '../../hooks/useForecast'
import { useEvents } from '../../hooks/useEvents'
import { useMe } from '../../hooks/useMe'
import { C, F } from './theme'
import { Card, SectionHead, Btn, Bar, Icon, LockedCard, gbp } from './prims'
import { deriveModel } from './derive'
import type { EventListItem, ForecastPayload, ForecastManualBaseline } from '../../types'
import type { ForecastPutBody } from '../../api'

// SCREEN EM-4 — Baseline & data. Link comparable past/present events (their
// REAL MERAC financials ride along on fc.baseline) and benchmark the current
// forecast against the average of the selected set. Money is integer PENCE and
// only formatted at the edge via gbp().

// --- helpers -----------------------------------------------------------------

// "Aug 2025" style label from an event's start date.
function whenLabel(iso: string): string {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
}

function avg(nums: number[]): number {
  if (!nums.length) return 0
  return nums.reduce((a, b) => a + b, 0) / nums.length
}

// A single benchmark comparison: forecast (cur) vs benchmark average (bench).
function BenchRow({ label, val, cur, bench, curLabel }: { label: string; val: string; cur: number; bench: number; curLabel: string }) {
  const max = Math.max(cur, bench) || 1
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
        <span style={{ fontFamily: F.body, fontSize: 11.5, fontWeight: 700, color: C.inkSoft }}>{label}</span>
        <span style={{ marginLeft: 'auto', fontFamily: F.mono, fontSize: 12, fontWeight: 700, color: C.ink }}>{val}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        <Bar pct={(cur / max) * 100} color={C.green} h={7} />
        <Bar pct={(bench / max) * 100} color={C.brassLt} h={7} />
      </div>
      <div style={{ display: 'flex', gap: 12, marginTop: 6, fontFamily: F.mono, fontSize: 10, color: C.inkFaint }}>
        <span><span style={{ color: C.green, fontWeight: 700 }}>forecast</span> {curLabel}</span>
        <span><span style={{ color: C.brassLt, fontWeight: 700 }}>benchmark</span> avg of selected</span>
      </div>
    </div>
  )
}

// --- main --------------------------------------------------------------------

export function BaselineTab({ eventId }: { eventId: number }) {
  const { fc, save } = useForecast(eventId)
  const { data: me } = useMe()
  const { data: events } = useEvents()

  if (!me?.hasFinancial) return <LockedCard label="Baseline financials need financial access" />
  if (!fc) return null

  // Candidate events to link: everything real (not another Idea) and not this event.
  const candidates: EventListItem[] = (events ?? []).filter((e) => e.status !== 'Idea' && e.id !== eventId)

  const baseline = fc.baseline
  const linkedIds = new Set(baseline.filter((b) => b.selected).map((b) => b.sourceEventId))

  // Toggle a candidate on/off. baselineLinks carries the whole selection set as
  // {sourceEventId,selected}; we add the id (selected:true) or flip an existing row.
  const toggle = (id: number) => {
    const existing = baseline.find((b) => b.sourceEventId === id)
    let next: Array<{ sourceEventId: number; selected: boolean }>
    if (existing) {
      next = baseline.map((b) => (b.sourceEventId === id ? { sourceEventId: b.sourceEventId, selected: !b.selected } : { sourceEventId: b.sourceEventId, selected: b.selected }))
    } else {
      next = [...baseline.map((b) => ({ sourceEventId: b.sourceEventId, selected: b.selected })), { sourceEventId: id, selected: true }]
    }
    void save({ baselineLinks: next })
  }

  // Benchmark: average over SELECTED baseline rows that carry real (non-null) data.
  const selRevenue = baseline.filter((b) => b.selected && b.revenuePence != null).map((b) => b.revenuePence as number)
  const selAtt = baseline.filter((b) => b.selected && b.attend != null).map((b) => b.attend as number)
  const selYield = baseline.filter((b) => b.selected && b.yieldPence != null).map((b) => b.yieldPence as number)
  const hasBench = selRevenue.length > 0

  const benchRevenue = avg(selRevenue)
  const benchAtt = avg(selAtt)
  const benchYield = avg(selYield)

  // Forecast (current-scenario) figures for the comparison bars.
  const model = deriveModel(fc)
  const curRevenue = model.grossRevPence
  const curAtt = fc.scenario.att
  const curYield = fc.scenario.yieldPence

  const beatsBench = curRevenue >= benchRevenue

  const applyBenchmark = () => {
    void save({ scenario: { att: Math.round(benchAtt), yieldPence: Math.round(benchYield), costAdj: fc.scenario.costAdj } })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(320px,1fr))', gap: 18 }}>
      {/* LEFT — comparable events picker */}
      <Card>
        <SectionHead icon="layers" label="Comparable events" />
        <div style={{ maxHeight: 360, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {candidates.length === 0 && (
            <div style={{ fontFamily: F.body, fontSize: 13, color: C.inkFaint, padding: '8px 2px' }}>No comparable events yet.</div>
          )}
          {candidates.map((ev) => {
            const linked = linkedIds.has(ev.id)
            const row = baseline.find((b) => b.sourceEventId === ev.id)
            const showMoney = linked && row?.revenuePence != null
            return (
              <button
                key={ev.id}
                type="button"
                onClick={() => toggle(ev.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 11, width: '100%', textAlign: 'left',
                  padding: '9px 11px', borderRadius: 10, cursor: 'pointer',
                  background: linked ? C.greenBg : C.card,
                  border: `1px solid ${linked ? C.greenSoft : C.lineSoft}`
                }}
              >
                <span style={{
                  width: 22, height: 22, borderRadius: 6, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: linked ? C.green : C.paper, border: `1px solid ${linked ? C.green : C.line}`
                }}>
                  {linked && <Icon name="check" size={13} color="#fff" />}
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontFamily: F.body, fontSize: 13, fontWeight: 700, color: C.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ev.name}</span>
                  <span style={{ display: 'block', fontFamily: F.mono, fontSize: 10.5, color: C.inkFaint, marginTop: 2 }}>
                    {whenLabel(ev.startDate)}
                    {showMoney && ` · ${gbp(row!.revenuePence as number)} · ${(row!.attend ?? 0).toLocaleString('en-GB')} att`}
                  </span>
                </span>
              </button>
            )
          })}
        </div>
      </Card>

      {/* RIGHT — benchmark */}
      <Card>
        <SectionHead icon="chart" label="Benchmark" />
        {!hasBench ? (
          <div style={{ fontFamily: F.body, fontSize: 13, color: C.inkFaint, padding: '4px 2px' }}>Link comparable events to build a benchmark.</div>
        ) : (
          <>
            <BenchRow label="Average revenue" val={gbp(benchRevenue)} cur={curRevenue} bench={benchRevenue} curLabel={gbp(curRevenue)} />
            <BenchRow label="Average attendance" val={Math.round(benchAtt).toLocaleString('en-GB')} cur={curAtt} bench={benchAtt} curLabel={curAtt.toLocaleString('en-GB')} />
            <BenchRow label="Average yield" val={gbp(benchYield)} cur={curYield} bench={benchYield} curLabel={gbp(curYield)} />

            <div style={{
              display: 'flex', alignItems: 'center', gap: 9, padding: '10px 12px', borderRadius: 10, marginBottom: 14,
              background: beatsBench ? C.greenBg : C.amberSoft, border: `1px solid ${(beatsBench ? C.green : C.amber)}33`
            }}>
              <Icon name={beatsBench ? 'checkc' : 'alert'} size={16} color={beatsBench ? C.green : C.amber} />
              <span style={{ fontFamily: F.body, fontSize: 12.5, fontWeight: 700, color: beatsBench ? C.green : C.amber }}>
                {beatsBench
                  ? `Forecast revenue is at or above the ${gbp(benchRevenue)} benchmark.`
                  : `Forecast revenue trails the ${gbp(benchRevenue)} benchmark.`}
              </span>
            </div>

            <Btn variant="soft" icon="check" disabled={!hasBench} onClick={applyBenchmark}>Apply benchmark to model</Btn>
          </>
        )}
      </Card>
    </div>
      <ManualBaselineCard fc={fc} save={save} emphasise={!hasBench} />
    </div>
  )
}

// "No comparable model" — a manual baseline the planner fills when there's no
// real comparable event to link. Stored on EventForecast.manual_baseline;
// "Apply" pushes attendance + yield into the forecast baseline so deriveModel
// picks them up. Money edited in pounds, stored/returned as integer pence.
function ManualBaselineCard({ fc, save, emphasise }: { fc: ForecastPayload; save: (b: Partial<ForecastPutBody>) => Promise<unknown>; emphasise: boolean }) {
  const [mb, setMb] = useState<ForecastManualBaseline>(fc.manualBaseline ?? { attendance: null, revenuePence: null, yieldPence: null, costsPence: null, note: '' })
  const seeded = useRef(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => { if (seeded.current) return; seeded.current = true; if (fc.manualBaseline) setMb(fc.manualBaseline) }, [fc.manualBaseline])
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])

  const update = (patch: Partial<ForecastManualBaseline>) => {
    const next = { ...mb, ...patch }
    setMb(next)
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => { void save({ manualBaseline: next }).catch(() => { /* swallow */ }) }, 500)
  }
  const apply = () => {
    void save({ attendanceFc: mb.attendance, ticketYieldPence: mb.yieldPence, scenario: { att: mb.attendance ?? 0, yieldPence: mb.yieldPence ?? 0, costAdj: fc.scenario.costAdj } }).catch(() => { /* swallow */ })
  }
  const canApply = (mb.attendance ?? 0) > 0 || (mb.yieldPence ?? 0) > 0

  return (
    <Card style={emphasise ? { borderColor: `${C.brass}55`, background: C.brassBg } : undefined}>
      <SectionHead icon="edit" label="Manual baseline · no comparable" right={
        <Btn variant="soft" small icon="check" disabled={!canApply} onClick={apply}>Apply to model</Btn>
      } />
      <div style={{ fontFamily: F.body, fontSize: 12.5, color: C.inkSoft, marginBottom: 14 }}>
        No comparable event to link? Enter your own baseline figures. “Apply” sets the forecast’s baseline attendance &amp; yield; expected revenue and costs are recorded for the business plan.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12 }}>
        <MbField label="Attendance">
          <IntInput value={mb.attendance} onChange={(v) => update({ attendance: v })} />
        </MbField>
        <MbField label="Ticket yield / head">
          <PenceInput value={mb.yieldPence} onChange={(v) => update({ yieldPence: v })} />
        </MbField>
        <MbField label="Expected revenue">
          <PenceInput value={mb.revenuePence} onChange={(v) => update({ revenuePence: v })} />
        </MbField>
        <MbField label="Expected costs">
          <PenceInput value={mb.costsPence} onChange={(v) => update({ costsPence: v })} />
        </MbField>
      </div>
      <textarea value={mb.note} onChange={(e) => update({ note: e.target.value })} placeholder="Assumptions / rationale (optional)"
        style={{ marginTop: 12, width: '100%', minHeight: 56, padding: '8px 10px', border: `1px solid ${C.line}`, borderRadius: 8, fontFamily: F.body, fontSize: 12.5, color: C.ink, resize: 'vertical', boxSizing: 'border-box' }} />
    </Card>
  )
}

function MbField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: 'block' }}>
      <span style={{ display: 'block', fontFamily: F.body, fontSize: 11, fontWeight: 700, color: C.inkSoft, marginBottom: 5 }}>{label}</span>
      {children}
    </label>
  )
}

const inputStyle: CSSProperties = { width: '100%', padding: '7px 9px', border: `1px solid ${C.line}`, borderRadius: 7, fontFamily: F.mono, fontSize: 12.5, color: C.ink, boxSizing: 'border-box' }

function IntInput({ value, onChange }: { value: number | null; onChange: (v: number | null) => void }) {
  return <input inputMode="numeric" value={value == null ? '' : String(value)} onChange={(e) => { const n = parseInt(e.target.value.replace(/[^0-9]/g, ''), 10); onChange(Number.isFinite(n) ? n : null) }} style={inputStyle} />
}

function PenceInput({ value, onChange }: { value: number | null; onChange: (v: number | null) => void }) {
  const [text, setText] = useState(value == null ? '' : (value / 100).toString())
  useEffect(() => { setText(value == null ? '' : (value / 100).toString()) }, [value])
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
      <span style={{ fontFamily: F.mono, fontSize: 12.5, color: C.inkFaint }}>£</span>
      <input inputMode="decimal" value={text} onChange={(e) => { const v = e.target.value; setText(v); const n = parseFloat(v); onChange(Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null) }} style={inputStyle} />
    </div>
  )
}
